/**
 * Which admin screen is showing, read from navigation STATE.
 *
 * The admin mobile shell mounts the RESIO header above its stack only outside
 * the full-screen routes (Chat, New Message, Edit Message). It used to learn
 * the current route from navigation EVENTS: the incoming screen's
 * `transitionStart` and the returning screen's `focus`. Those fire after the
 * stack has already committed the screen swap, and this stack has animations
 * disabled, so the header toggled one commit later and the screen painted one
 * frame with the wrong header state, then jumped by the header's height
 * (measured on web: the Messages title at y=28 for a frame, then y=88).
 *
 * AdminNavigator is itself a screen of the root stack, so the admin stack's
 * state lives on its own route inside the root state. Selecting the focused
 * nested route from there re-renders the shell in the same pass as the stack,
 * and header and screen land in one commit.
 */
import { useNavigationState, useRoute } from '@react-navigation/native';
import type { AdminStackParamList } from './AdminNavigator';

export type AdminRouteName = keyof AdminStackParamList;

export const ADMIN_INITIAL_ROUTE: AdminRouteName = 'ResidentList';

/** Screens that own the whole viewport (their own header, no shell header). */
export const FULLSCREEN_ADMIN_ROUTES: ReadonlySet<AdminRouteName> = new Set<AdminRouteName>([
  'Chat',
  'CreateMessage',
  'EditMessage',
]);

export const isFullscreenAdminRoute = (name: AdminRouteName): boolean =>
  FULLSCREEN_ADMIN_ROUTES.has(name);

// The slices of navigation state this reads. A nested state can still be
// partial (no index) before its navigator has initialised.
type RouteLike = { key: string; name: string; state?: StateLike };
type StateLike = { index?: number; routes?: RouteLike[] } | undefined;

export function selectNestedRouteName(
  rootState: StateLike,
  ownKey: string,
  fallback: AdminRouteName,
): AdminRouteName {
  const own = rootState?.routes?.find((route) => route.key === ownKey);
  const routes = own?.state?.routes;
  if (!routes || routes.length === 0) return fallback;
  const index = typeof own?.state?.index === 'number' ? own.state.index : routes.length - 1;
  return (routes[index]?.name as AdminRouteName | undefined) ?? fallback;
}

export function useAdminCurrentRoute(): AdminRouteName {
  const { key } = useRoute();
  return useNavigationState((state) =>
    selectNestedRouteName(state as unknown as StateLike, key, ADMIN_INITIAL_ROUTE),
  );
}
